<template>
  <v-row justify="center">
    <v-dialog 
      :value="value"
      max-width="800px"
      transition="dialog-bottom-transition"
      scrollable
      @input="$emit('input', $event)"
      class="custom-audit-modal-grouped"
    >
    <v-card v-if="entityList?.length">
      <v-card-title>
        <div class="d-flex flex-column">
          <h5 class="text-h5 font-weight-medium">{{ $t('modules.customdata.audit.audit_dialog_title') }}</h5>
          <div v-if="auditStartTimeInput && auditStartTimeInput.trim()" class="audit-time-info mt-2">
            <v-chip 
              small 
              color="primary" 
              text-color="white"
              class="mr-2"
            >
              <v-icon small left>mdi-clock-start</v-icon>
              {{ $t('modules.customdata.audit.audit_started_at') }}: {{ auditStartTimeInput.trim() }}
            </v-chip>
          </div>
        </div>
      </v-card-title>
        <v-card-text ref="auditModalBody" class="pt-5">
          <v-container class="pa-0">
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-row class="mb-4">
                <v-col cols="12" sm="6">
                  <v-text-field
                    ref="auditStartTimeField"
                    v-model="auditStartTimeInput"
                    v-mask="'##:##'"
                    :label="$t('modules.customdata.audit.audit_start_time_label')"
                    prepend-icon="mdi-clock-start"
                    append-icon="mdi-clock-outline"
                    placeholder="HH:mm"
                    :rules="auditStartTimeRules"
                    hide-details="auto"
                    @click:append="modalAuditStartTime = true"
                    @input="onAuditStartTimeInput"
                  ></v-text-field>
                  <v-dialog
                    ref="dialogAuditStartTime"
                    v-model="modalAuditStartTime"
                    :return-value.sync="auditStartTimeInput"
                    persistent
                    width="290px"
                  >
                    <v-time-picker
                      v-if="modalAuditStartTime"
                      v-model="auditStartTimeInput"
                      full-width
                      :locale="$i18n.locale"
                      format="24hr"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="modalAuditStartTime = false">{{ $t('common.cancel') }}</v-btn>
                      <v-btn text color="primary" @click="confirmAuditStartTimePicker">{{ $t('common.confirm') }}</v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    ref="auditEndTimeField"
                    v-model="auditEndTimeInput"
                    v-mask="'##:##'"
                    :label="$t('modules.customdata.audit.audit_end_time_label')"
                    prepend-icon="mdi-clock-end"
                    append-icon="mdi-clock-outline"
                    placeholder="HH:mm"
                    :rules="auditEndTimeRules"
                    hide-details="auto"
                    @click:append="modalAuditEndTime = true"
                  ></v-text-field>
                  <v-dialog
                    ref="dialogAuditEndTime"
                    v-model="modalAuditEndTime"
                    :return-value.sync="auditEndTimeInput"
                    persistent
                    width="290px"
                  >
                    <v-time-picker
                      v-if="modalAuditEndTime"
                      v-model="auditEndTimeInput"
                      full-width
                      :locale="$i18n.locale"
                      format="24hr"
                    >
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="modalAuditEndTime = false">{{ $t('common.cancel') }}</v-btn>
                      <v-btn text color="primary" @click="confirmAuditEndTimePicker">{{ $t('common.confirm') }}</v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
              </v-row>
              <div
                v-for="(group, groupName) in groupedQuestions"
                :key="groupName"
                class="question-group mb-10"
              >
                <div 
                  v-if="groupName !== 'ungrouped'"
                  class="group-header mb-4 pa-3 rounded-lg"
                >
                  <h6 class="text-h6 font-weight-medium mb-0 text--primary">
                    {{ groupName }}
                  </h6>
                  <small class="text-caption text--secondary">
                    {{ group.length }} {{ group.length > 4 ? $t('modules.customdata.audit.audit_dialog_questions_count_2') : $t('modules.customdata.audit.audit_dialog_questions_count_1') }}
                  </small>
                </div>

                <v-row
                   v-for="item in group"
                  :key="`${groupName}-${item.id}`"
                  :class="`question-row item-${item.id} ${getDynamicOrder(item.id) ? 'dynamic-order-' + getDynamicOrder(item.id) : ''}`"
                >
                   <v-col cols="12" v-if="item.audit_question_is_visible">
                                         <div class="question-item pa-4 rounded">
                       <div class="d-flex align-center">
                         <p class="question-title mb-0 text-h6 text--primary flex-grow-1">{{ item.audit_question }}</p>
                         <v-icon 
                           v-if="item.audit_question_description" 
                           class="ml-2 info-icon"
                           @click="toggleDescription(item.id)"
                           :color="visibleDescriptions[item.id] ? 'primary' : 'grey'"
                         >
                           {{ visibleDescriptions[item.id] ? 'mdi-information' : 'mdi-information-outline' }}
                         </v-icon>
                       </div>
                       
                       <!-- Question description -->
                       <p v-if="item.audit_question_description && visibleDescriptions[item.id]" class="question-description mt-2 mb-3" v-html="item.audit_question_description"></p>

                       <!-- STRING FIELDS -->
                        <v-text-field
                          v-if="['STRING'].includes(item.audit_question_field_type)"
                          :label="`${$t('modules.customdata.audit.audit_field_placeholder')}${isFieldRequired(item) ? '*' : ''}`"
                          :required="isFieldRequired(item)"
                          :rules="isFieldRequired(item) ? [dynamicRules.required(item.id)] : []"
                        v-model.trim="itemObjCopy[getFieldKey(item)]"
                        class="mt-0"
                      ></v-text-field>

                                              <!-- TEXTAREA FIELDS -->
                        <v-textarea
                          v-if="['TEXT'].includes(item.audit_question_field_type)"
                          :label="`${$t('modules.customdata.audit.audit_field_placeholder')}${isFieldRequired(item) ? '*' : ''}`"
                          :required="isFieldRequired(item)"
                          :rules="isFieldRequired(item) ? [dynamicRules.required(item.id)] : []"
                        v-model.trim="itemObjCopy[`text_${item.id}`]"
                        auto-grow
                        outlined
                        rows="2"
                        class="mt-3"
                      ></v-textarea>

                       <!-- INTEGER FIELDS -->
                        <v-text-field
                          v-if="['INTEGER', 'BIGINT'].includes(item.audit_question_field_type)"
                          :label="`${$t('modules.customdata.audit.audit_field_placeholder')}${isFieldRequired(item) ? '*' : ''}`"
                          :required="isFieldRequired(item)"
                          :rules="isFieldRequired(item) ? [dynamicRules.required(item.id)] : []"
                        v-model.trim="itemObjCopy[item.audit_question]"
                        class="mt-0"
                      ></v-text-field>

                       <!-- FLOAT FIELDS -->
                        <v-text-field
                          v-if="['FLOAT'].includes(item.audit_question_field_type)"
                          :label="`${$t('modules.customdata.audit.audit_field_placeholder')}${isFieldRequired(item) ? '*' : ''}`"
                          :required="isFieldRequired(item)"
                          :rules="isFieldRequired(item) ? [dynamicRules.required(item.id)] : []"
                        v-model.trim="itemObjCopy[item.audit_question]"
                        class="mt-0"
                      ></v-text-field>

                       <!-- BOOLEAN FIELDS -->
                        <v-checkbox
                          v-if="['BOOLEAN'].includes(item.audit_question_field_type)"
                          v-model="itemObjCopy[getFieldKey(item)]"
                          :required="isFieldRequired(item)"
                          :rules="isFieldRequired(item) ? [dynamicRules.required(item.id)] : []"
                        hide-details="auto"
                        class="mt-0"
                      ></v-checkbox>

                                               <!-- RADIO FIELDS -->
                       <div v-if="['RADIO'].includes(item.audit_question_field_type)" class="mt-3">
                          <v-radio-group
                            v-model="itemObjCopy[getFieldKey(item)]"
                            :required="isFieldRequired(item)"
                            :rules="isFieldRequired(item) ? [dynamicRules.required(item.id)] : []"
                           hide-details="auto"
                           class="mt-0"
                           @change="() => $nextTick(() => resetSubsequentAnswers(item.id))"
                         >
                           <v-radio
                             v-for="option in getDisplayVariantsForItem(item)"
                             :key="`${item.id}-${option.text}-${option.value}`"
                             :label="option.text"
                             :value="option.text"
                             class="mb-2"
                           ></v-radio>
                         </v-radio-group>
                       </div>

                       <!-- SELECT FIELDS -->
                       <div v-if="['SELECT'].includes(item.audit_question_field_type)" class="mt-3">
                          <v-select
                            v-model="itemObjCopy[getFieldKey(item)]"
                            :items="getDisplayVariantsForItem(item)"
                            item-text="text"
                            item-value="text"
                            :label="`${$t('modules.customdata.audit.audit_field_placeholder')}${isFieldRequired(item) ? '*' : ''}`"
                            :required="isFieldRequired(item)"
                            :rules="isFieldRequired(item) ? [dynamicRules.required(item.id)] : []"
                           outlined
                           dense
                           class="mt-0"
                           @change="() => $nextTick(() => resetSubsequentAnswers(item.id))"
                         ></v-select>
                       </div>

                       <!-- MULTISELECT FIELDS -->
                       <div v-if="['MULTISELECT'].includes(item.audit_question_field_type)" class="mt-3">
                         <!-- Hidden field for validation -->
                          <v-text-field
                            v-model="itemObjCopy[getFieldKey(item)]"
                            :rules="isFieldRequired(item) ? [dynamicRules.multiselectRequired(item.id)] : []"
                           style="display: none;"
                           hide-details="auto"
                         ></v-text-field>
                         
                          <v-checkbox
                            v-for="option in getDisplayVariantsForItem(item)"
                            :key="`${item.id}-${option.text}-${option.value}`"
                            v-model="itemObjCopy[getFieldKey(item)]"
                            :value="option.text"
                            :label="option.text"
                            :required="isFieldRequired(item)"
                           hide-details="auto"
                           class="mb-2 multiselect-item"
                           @change="() => $nextTick(() => resetSubsequentAnswers(item.id))"
                         ></v-checkbox>
                       </div>

                       <!-- FILE FIELDS (gallery + camera + thumbnails, BAT121-780) -->
                        <div v-if="['FILE'].includes(item.audit_question_field_type)" class="audit-file-picker mt-2">
                          <div class="text-body-2 font-weight-medium mb-2">
                            {{ $t('modules.customdata.audit.audit_field_file_placeholder') }}<span v-if="isFieldRequired(item)">*</span>
                          </div>
                          <v-input
                            :value="(auditFileAttachments[getFieldKey(item)] || []).length"
                            dense
                            hide-details="auto"
                            :rules="auditFileAttachmentRules(item)"
                          >
                            <div class="audit-file-picker__actions d-flex flex-wrap align-center">
                              <v-btn
                                small
                                depressed
                                color="secondary"
                                :disabled="loading || fileUploading"
                                type="button"
                                class="ma-0"
                                @click="openGalleryPick(item)"
                              >
                                <v-icon left small>mdi-image-multiple-outline</v-icon>
                                {{ $t('modules.customdata.audit.audit_file_pick_gallery') }}
                              </v-btn>
                              <v-btn
                                small
                                depressed
                                color="primary"
                                :disabled="loading || fileUploading"
                                type="button"
                                class="ma-0"
                                @click="openCameraCapture(item)"
                              >
                                <v-icon left small>mdi-camera</v-icon>
                                {{ $t('modules.customdata.audit.audit_file_take_photo') }}
                              </v-btn>
                            </div>
                          </v-input>
                          <div
                            v-if="(auditFileAttachments[getFieldKey(item)] || []).length > 0"
                            class="audit-file-picker__previews mt-3 d-flex flex-wrap"
                          >
                            <div
                              v-for="entry in (auditFileAttachments[getFieldKey(item)] || [])"
                              :key="`${getFieldKey(item)}-${entry.uid}`"
                              class="audit-file-picker__thumb"
                            >
                              <v-img
                                :src="entry.previewUrl"
                                aspect-ratio="1"
                                contain
                                class="rounded grey lighten-3"
                                max-width="104"
                                max-height="104"
                              />
                              <v-chip x-small label class="mt-1">{{ auditUploadSourceLabel(entry.source) }}</v-chip>
                              <v-btn
                                icon
                                x-small
                                type="button"
                                class="audit-file-picker__remove"
                                :aria-label="$t('modules.customdata.audit.audit_file_remove_photo')"
                                @click="removeAuditFileAttachment(item, entry.uid)"
                              >
                                <v-icon small color="grey darken-2">mdi-close</v-icon>
                              </v-btn>
                            </div>
                          </div>
                        </div>

                    </div>
                  </v-col>
                </v-row>

                                   <!-- Audit completion message -->
                 <div v-if="isAuditCompleted" class="audit-completed-message mt-6 rounded-lg">
                   <v-alert
                     type="success"
                     border="left"
                     colored-border
                     elevation="2"
                   >
                     <span class="font-weight-medium mb-0">
                       {{ $t('modules.customdata.audit.audit_dialog_completed') }}
                     </span>
                   </v-alert>
                 </div>

              </div>
            </v-form>
            <input
              ref="auditGalleryHiddenInput"
              type="file"
              class="audit-file-picker__hidden-input"
              accept="image/*"
              multiple
              @change="onHiddenGalleryInputChange"
            />
            <input
              ref="auditCameraFallbackInput"
              type="file"
              class="audit-file-picker__hidden-input"
              accept="image/*"
              capture="environment"
              @change="onHiddenCameraFallbackChange"
            />
          </v-container>
        </v-card-text>
        
        <v-card-actions class="question-actions px-4 py-3 flex-wrap">
           <div class="total-score w-100-percent d-flex align-center flex-wrap">
             {{ $t('modules.customdata.audit.audit_dialog_total_score') }} <strong class="ml-2">{{ totalScore }} ({{ totalScorePercentage }}%)</strong>
             <span v-if="currentAuditType" class="audit-type-info ml-4">
               {{ currentAuditType === 'full' ? $t('modules.customdata.audit.audit_dialog_full') : $t('modules.customdata.audit.audit_dialog_partial') }}
             </span>
           </div>

           <small class="mt-3">* {{ $t('modules.customdata.audit.audit_dialog_info') }}</small>

          <v-spacer></v-spacer>
           <div class="mt-3">
          <v-btn 
            outlined
            small 
            @click="closeAuditModal"
            color="secondary"
          >
            {{$t('modules.customdata.audit.audit_dialog_close_btn')}}
          </v-btn>
          <v-btn 
            color="primary" 
            elevation
            small
               :disabled="isSendAuditDisabled"
            :loading="loading"
            @click="send"
               class="ml-2"
          >
            {{$t('modules.customdata.audit.audit_dialog_send_btn')}}
          </v-btn>
           </div>
        </v-card-actions>
      </v-card>

      <v-card
        color="primary"
        dark
        v-else
        class="rounded-lg"
      >
        <v-card-text class="pt-5">
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>

    </v-dialog>

    <v-dialog
      v-model="cameraCaptureVisible"
      max-width="480px"
      persistent
    >
      <v-card>
        <v-card-title class="text-subtitle-1 d-flex align-center">
          <span class="flex-grow-1">{{ $t('modules.customdata.audit.audit_camera_dialog_title') }}</span>
          <v-btn
            icon
            small
            outlined
            color="primary"
            class="audit-camera-flip-btn"
            type="button"
            :disabled="cameraCaptureShooting"
            :aria-label="$t('modules.customdata.audit.audit_camera_flip')"
            @click="flipAuditCameraFacing"
          >
            <v-icon small>mdi-camera-flip-outline</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p v-if="cameraCaptureError" class="caption error--text mb-2">{{ cameraCaptureError }}</p>
          <video
            v-show="!cameraCaptureError"
            ref="auditCameraVideo"
            autoplay
            playsinline
            muted
            class="audit-camera-video grey darken-4 rounded"
            style="width: 100%; max-height: 360px;"
          />
          <canvas ref="auditCameraCanvas" class="d-none"></canvas>
        </v-card-text>
        <v-card-actions class="audit-camera-dialog-actions pa-3">
          <div class="audit-camera-dialog-actions__secondary d-flex align-center flex-grow-1">
            <v-btn
              text
              color="secondary"
              class="flex-shrink-1"
              type="button"
              :disabled="cameraCaptureShooting"
              @click="openCameraFallbackFromDialog"
            >
              {{ $t(isAuditMobileCaptureDevice ? 'modules.customdata.audit.audit_camera_use_device_camera' : 'modules.customdata.audit.audit_camera_use_device_picker') }}
            </v-btn>
            <v-spacer />
            <v-btn text type="button" @click="closeCameraCapture">{{ $t('common.cancel') }}</v-btn>
          </div>
          <v-btn
            color="primary"
            class="audit-camera-dialog-actions__capture"
            type="button"
            :loading="cameraCaptureShooting"
            :disabled="!!cameraCaptureError || !cameraCaptureStreamReady"
            @click="captureCameraStill"
          >
            {{ $t('modules.customdata.audit.audit_camera_capture') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import { mapGetters } from "vuex";
import Vue from "vue";
import axios from "axios";
import { normalizeActivityName } from '@/modules/CustomData/utils/activityNormalize';
import { isDefaultAnswerVariant } from '@/modules/CustomData/utils/auditMode.js';

const ACTIVITY_MODEL = 'activity';
const AUDIT_UPLOAD_SOURCE_CAMERA = 'Camera';
const AUDIT_UPLOAD_SOURCE_GALLERY = 'Gallery';

function createAuditUploadUniqueId() {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function getAuditUploadFileExtension(file) {
	const name = String(file?.name || '').toLowerCase();
	const dotIdx = name.lastIndexOf('.');
	if (dotIdx > 0 && dotIdx < name.length - 1) {
		const ext = name.slice(dotIdx);
		if (/^\.(jpe?g|png|gif|webp|bmp|heic|heif)$/.test(ext)) {
			return ext;
		}
	}
	const mime = typeof file?.type === 'string' ? file.type : '';
	if (mime === 'image/png') {
		return '.png';
	}
	if (mime === 'image/webp') {
		return '.webp';
	}
	if (mime === 'image/gif') {
		return '.gif';
	}
	return '.jpg';
}

function normalizeAuditUploadFile(file, item, source, store) {
	const questionId = item?.id ?? '0';
	const user = store?.state?.kwizbot?.user;
	const userId = user?.id ?? user?.user_id ?? user?.data?.user?.id ?? '0';
	const tag = source === AUDIT_UPLOAD_SOURCE_CAMERA ? 'camera' : 'gallery';
	const ext = getAuditUploadFileExtension(file);
	const uniqueName = `audit-${tag}-q${questionId}-u${userId}-${Date.now()}-${createAuditUploadUniqueId()}${ext}`;
	if (file.name === uniqueName) {
		return file;
	}
	return new File([file], uniqueName, {
		type: file.type || 'image/jpeg',
		lastModified: file.lastModified,
	});
}

export default {
  name: 'DynamicAuditModal',

  props: {
    value: {
      type: Boolean,
      default: false,
      required: true,
    },
    item_obj: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },

       data: () => ({
    entityList: null,
    valid: false,
    loading: false, 
    fileUploading: false,
    entityName: 'audit_questions',
    auditTableList: 'audit_table_list',
    defaultTableId: 1, // ID default table
    itemObjCopy: {},
    fileUrls: {},
    currentAuditType: null,
    isResetting: false,
    visibleDescriptions: {},
    auditStartTime: null,
    auditStartTimeInput: '',
    auditEndTimeInput: '',
    modalAuditStartTime: false,
    modalAuditEndTime: false,
    auditAttachmentUidSeq: 0,
    auditFileAttachments: {},
    galleryPickQuestion: null,
    fallbackCameraPickQuestion: null,
    cameraCaptureVisible: false,
    cameraCaptureQuestion: null,
    cameraCaptureError: '',
    cameraCaptureShooting: false,
    cameraCaptureStreamReady: false,
    cameraStream: null,
    cameraCaptureFacingMode: 'environment',

  }),

  watch: {
    value: {
      handler(newVal, oldVal) {
        if (!newVal && oldVal) {
          this.cameraCaptureVisible = false;
          this.clearFileFields();
        }
        if (newVal && !oldVal) {
          this.auditStartTime = new Date();
          this.auditStartTimeInput = this.formatTimeToHHMM(this.auditStartTime);
          this.auditEndTimeInput = '';
        }
      },
      immediate: true
    },

    item_obj: {
      handler(newVal) {
        this.revokeAllAuditAttachmentPreviews();
        this.auditFileAttachments = {};
        this.itemObjCopy = JSON.parse(JSON.stringify(newVal || {}));
      },
      immediate: true
    },

    cameraCaptureVisible(newVal) {
      if (newVal === true) return;
      this.cameraCaptureShooting = false;
      this.teardownAuditCameraCapture();
      this.cameraCaptureQuestion = null;
      this.cameraCaptureError = '';
      this.cameraCaptureFacingMode = 'environment';
    },

    entityList: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.initializeDefaultValues();
        }
      },
      immediate: false
    },

     itemObjCopy: {
       handler(newVal, oldVal) {
         if (!oldVal || !this.entityList || this.isResetting) return;
         
         // Check if any answer changed
         for (const item of this.entityList) {
           if (item.audit_question_is_visible) {
             const fieldKey = this.getFieldKey(item);
             const oldValue = oldVal[fieldKey];
             const newValue = newVal[fieldKey];
             
             // Skip if values are the same
             if (oldValue === newValue) continue;
             
             // For MULTISELECT, compare arrays
             if (item.audit_question_field_type === 'MULTISELECT') {
               const oldArray = Array.isArray(oldValue) ? oldValue : [];
               const newArray = Array.isArray(newValue) ? newValue : [];
               if (JSON.stringify(oldArray.sort()) !== JSON.stringify(newArray.sort())) {
                 this.$nextTick(() => {
                   this.resetSubsequentAnswers(item.id);
                 });
                 break;
               }
             } else {
               // For other field types, simple comparison
               if (oldValue !== newValue) {
                 this.$nextTick(() => {
                   this.resetSubsequentAnswers(item.id);
                 });
                 break;
               }
             }
           }
         }
       },
       deep: true
     }
  },

  computed: {
    ...mapGetters([
      'engine_settings',
      'rules',
    ]),

    isUnifiedAuditMode() {
      return this.item_obj?.audit_mode === 'unified';
    },

    activityNormalized() {
      if (this.isUnifiedAuditMode) return '';
      return this.item_obj?.activity_normalized || '';
    },

    activityFilteredEntityList() {
      if (!this.entityList) return [];
      if (this.isUnifiedAuditMode) {
        return this.entityList.filter(item => this.isQuestionRelevantForUnified(item));
      }
      if (!this.activityNormalized) return this.entityList;
      return this.entityList.filter(item => this.isQuestionRelevantForActivity(item));
    },

    // Get current user role
    currentUserRole() {
      const user = this.$store.state.kwizbot.user;
      return user?.role?.alias || null;
    },

    /** Touch + coarse pointer or narrow viewport — native camera vs file picker in dialog */
    isAuditMobileCaptureDevice() {
      if (typeof window === 'undefined') {
        return false;
      }
      const touch = typeof window.ontouchstart !== 'undefined' && navigator.maxTouchPoints > 0;
      if (!touch) {
        return false;
      }
      const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
      const narrowViewport = window.matchMedia?.('(max-width: 600px)')?.matches ?? false;
      return coarsePointer || narrowViewport;
    },

    // Dynamic validation rules that only apply to visible fields
    dynamicRules() {
      return {
        required: (questionId) => (value) => {
          // Only validate if the question is visible
          if (!this.isQuestionVisible(questionId)) {
            return true; // Skip validation for hidden fields
          }
          return this.rules.required(value);
        },
        // Special validation for MULTISELECT fields
        multiselectRequired: (questionId) => (value) => {
          // Only validate if the question is visible
          if (!this.isQuestionVisible(questionId)) {
            return true; // Skip validation for hidden fields
          }
          // For MULTISELECT, check if array has any values
          if (Array.isArray(value)) {
            return value.length > 0 || this.$t('modules.customdata.audit.audit_field_required');
          }
          // If value is not an array, it's empty
          return this.$t('modules.customdata.audit.audit_field_required');
        },
        // Special validation for FILE fields
        fileRequired: (questionId) => (value) => {
          // Only validate if the question is visible
          if (!this.isQuestionVisible(questionId)) {
            return true; // Skip validation for hidden fields
          }
          // For FILE fields, check if array has any files
          if (Array.isArray(value)) {
            return value.length > 0 || this.$t('modules.customdata.audit.audit_field_required');
          }
          // If value is not an array, it's empty
          return this.$t('modules.customdata.audit.audit_field_required');
        }
      };
    },

    totalQuestions() {
      if (!this.entityList) return 0;
      return this.activityFilteredEntityList.filter(item => item.audit_question_is_visible).length;
    },

                        totalScore() {
       if (!this.entityList || !this.itemObjCopy) return 0;
       
       let score = 0;
       
       this.activityFilteredEntityList.forEach(item => {
         if (item.audit_question_is_visible && 
             this.isQuestionVisible(item.id) &&
             ['RADIO', 'SELECT', 'MULTISELECT'].includes(item.audit_question_field_type) && 
             this.itemObjCopy[this.getFieldKey(item)]) {
           
           const selectedValue = this.itemObjCopy[this.getFieldKey(item)];
           const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
           
           if (item.audit_question_field_type === 'MULTISELECT') {
             const selectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
             selectedValues.forEach(selectedText => {
               const selectedOption = this.resolveVariantByActivity(variants, selectedText);
               if (selectedOption && selectedOption.score !== undefined) {
                 score += this.getScoreFromVariant(selectedOption);
               }
             });
           } else {
             const selectedOption = this.resolveVariantByActivity(variants, selectedValue);
             if (selectedOption && selectedOption.score !== undefined) {
               score += this.getScoreFromVariant(selectedOption);
             }
           }
         }
       });
       
       return score < 0 ? 0 : score;
     },

    maxPossibleScore() {
      if (!this.entityList) return 0;
      
      let maxScore = 0;
      
      this.activityFilteredEntityList.forEach(item => {
        if (item.audit_question_is_visible && 
            this.isQuestionVisible(item.id) &&
            ['RADIO', 'SELECT', 'MULTISELECT'].includes(item.audit_question_field_type)) {
          
          const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
          const displayVariants = this.getDisplayVariantsForItem(item);
          
          if (item.audit_question_field_type === 'MULTISELECT') {
            let questionMaxScore = 0;
            displayVariants.forEach(option => {
              if (option.score !== undefined) {
                const optionScore = this.getScoreFromVariant(option);
                if (optionScore > 0) questionMaxScore += optionScore;
              }
            });
            maxScore += questionMaxScore;
          } else {
            let questionMaxScore = 0;
            displayVariants.forEach(option => {
              if (option.score !== undefined) {
                const optionScore = this.getScoreFromVariant(option);
                if (optionScore > questionMaxScore) questionMaxScore = optionScore;
              }
            });
            maxScore += questionMaxScore;
          }
        }
      });
      
      return maxScore;
    },

    totalScorePercentage() {
      if (this.maxPossibleScore === 0) return 0;
      
      const percentage = (this.totalScore / this.maxPossibleScore) * 100;
      return Math.round(percentage); // Round to integer
    },

    dynamicQuestionOrder() {
      const order = {};
      this.visibleQuestions.forEach((q, i) => {
        order[q.id] = i + 1;
      });
      return order;
    },

    groupedQuestions() {
      if (!this.entityList) return {};
      
      const flowOrder = this.visibleQuestions;
      const flowOrderMap = new Map(flowOrder.map((q, i) => [q.id, i]));
      const getFlowIndex = (id) => flowOrderMap.has(id) ? flowOrderMap.get(id) : 999999;

      const grouped = {};
      const filteredList = this.activityFilteredEntityList;

      filteredList.forEach(item => {
        if (item.audit_question_is_visible && this.isQuestionVisible(item.id)) {
          const groupName = item.audit_question_group || 'ungrouped';
          if (!grouped[groupName]) {
            grouped[groupName] = [];
          }
          grouped[groupName].push(item);
        }
      });

      // Sort items within each group by flow order (next question always follows previous)
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => getFlowIndex(a.id) - getFlowIndex(b.id));
      });

      // Sort groups alphabetically
      const sortedGroups = {};
      Object.keys(grouped)
        .sort((a, b) => {
          if (a === 'ungrouped') return 1;
          if (b === 'ungrouped') return -1;
          return a.localeCompare(b);
        })
        .forEach(key => {
          sortedGroups[key] = grouped[key];
        });

      return sortedGroups;
    },

              visibleQuestions() {
       if (!this.entityList) return [];

       const list = this.activityFilteredEntityList;
       const visible = [];
       let currentQuestionIds = [1]; // Start with first question

       while (currentQuestionIds.length > 0) {
         const currentQuestionId = currentQuestionIds.shift(); // Get next question ID
         const question = list.find(item => item.id === currentQuestionId && item.audit_question_is_visible);
         
         if (!question) continue;
         
         visible.push(question);
         
         // Determine next questions based on selected answer
        if (this.itemObjCopy[this.getFieldKey(question)]) {
          const selectedValue = this.itemObjCopy[this.getFieldKey(question)];
           const variants = this.parseAnswerVariants(question.audit_question_answer_variant, { questionId: question.id, audit_question: question.audit_question });
           
           let selectedOption = null;
           if (question.audit_question_field_type === 'MULTISELECT') {
             const selectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
             for (const sv of selectedValues) {
               const opt = this.resolveVariantByActivity(variants, sv);
               if (opt && opt.nextId) {
                 selectedOption = opt;
                 break;
               }
             }
           } else if (question.audit_question_field_type === 'FILE') {
             selectedOption = this.resolveFileFlowOption(question, selectedValue);
           } else {
             selectedOption = this.resolveAuditFlowOptionNonMulti(question, selectedValue);
           }
           
           if (selectedOption && selectedOption.nextId && selectedOption.nextId !== '') {
             const nextIds = this.parseNextIds(selectedOption);
             currentQuestionIds.push(...nextIds);
           }
         }
       }
       
       return visible;
     },

          

    isAuditCompleted() {
       if (!this.entityList) return false;

       const visible = this.visibleQuestions || [];
       if (!visible.length) {
         return false;
       }

       for (const item of visible) {
         if (!this.isFieldRequired(item) || item.audit_question_field_type === 'FILE') {
           continue;
         }
         const fieldKey = this.getFieldKey(item);
         const value = this.itemObjCopy[fieldKey];
         const empty = item.audit_question_field_type === 'MULTISELECT'
           ? (!Array.isArray(value) || value.length === 0)
           : (!value || value === '');
         if (empty) {
           return false;
         }
       }

       const last = visible[visible.length - 1];
       if (last.audit_question_field_type === 'FILE') {
         return !this.isFieldRequired(last) || this.hasAuditFileAttachments(last);
       }

       const fieldKey = this.getFieldKey(last);
       const selectedValue = this.itemObjCopy[fieldKey];
       const lastAnswered = last.audit_question_field_type === 'MULTISELECT'
         ? (Array.isArray(selectedValue) && selectedValue.length > 0)
         : !!(selectedValue && selectedValue !== '');
       if (!lastAnswered) {
         return false;
       }

       const variants = this.parseAnswerVariants(last.audit_question_answer_variant, {
         questionId: last.id,
         audit_question: last.audit_question,
       });
       let selectedOption = null;
       if (last.audit_question_field_type === 'MULTISELECT') {
         const selectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
         for (const sv of selectedValues) {
           const opt = this.resolveVariantByActivity(variants, sv);
           if (opt && opt.nextId) {
             selectedOption = opt;
             break;
           }
         }
       } else {
         selectedOption = this.resolveAuditFlowOptionNonMulti(last, selectedValue);
       }

       const emptyNext = !selectedOption || !selectedOption.nextId;
       const nextIds = emptyNext ? [] : this.parseNextIds(selectedOption);
       const remainingNonFile = nextIds.filter((id) => {
         const q = this.activityFilteredEntityList.find((item) => item.id === id);
         if (!q) {
           return true;
         }
         return q.audit_question_field_type !== 'FILE';
       });
       return emptyNext || remainingNonFile.length === 0;
    },

    isSendAuditDisabled() {
      return (!this.valid && !this.isAuditCompleted) || !this.item_obj || this.loading;
    },

    auditStartTimeRules() {
      return [
        (v) => {
          const value = (v == null ? '' : String(v)).trim();
          if (!value) {
            return this.$t('modules.customdata.audit.audit_field_required');
          }
          return this.isValidAuditTimeString(value)
            || this.$t('modules.customdata.audit.audit_time_invalid_format');
        },
      ];
    },

    auditEndTimeRules() {
      return [
        (v) => {
          const value = (v == null ? '' : String(v)).trim();
          if (!value) {
            return true;
          }
          if (!this.isValidAuditTimeString(value)) {
            return this.$t('modules.customdata.audit.audit_time_invalid_format');
          }
          const start = (this.auditStartTimeInput || '').trim();
          if (!this.isValidAuditTimeString(start)) {
            return true;
          }
          if (this.auditTimeToMinutes(value) < this.auditTimeToMinutes(start)) {
            return this.$t('modules.customdata.audit.audit_end_before_start');
          }
          return true;
        },
      ];
    },
    
  },

  methods: {
    getFieldKey(q) {
      if (q.audit_question_field_type === 'TEXT' || q.audit_question_field_type === 'STRING') return `text_${q.id}`;
      if (['RADIO', 'SELECT', 'BOOLEAN', 'MULTISELECT', 'FILE'].includes(q.audit_question_field_type)) return `value_${q.id}`;
      return q.audit_question;
    },

    /**
     * Resolves audit variant for flow (nextId) for non-MULTISELECT questions.
     * FILE questions use variants with column metadata (no matching by text).
     */
    resolveAuditFlowOptionNonMulti(item, selectedValue) {
      const variants = this.parseAnswerVariants(item.audit_question_answer_variant, {
        questionId: item.id,
        audit_question: item.audit_question,
      });

      if (item.audit_question_field_type === 'FILE') {
        if (
          Array.isArray(selectedValue) &&
          selectedValue.length > 0 &&
          selectedValue.every((f) => f instanceof File)
        ) {
          return variants.find((v) => v.column) || null;
        }
        return null;
      }

      return this.resolveVariantByActivity(variants, selectedValue);
    },

    /**
     * FILE nextId: with photos use the answered variant; if the field is optional, still follow nextId without files
     * so later questions stay reachable (e.g. CAM/CR skip photo).
     */
    resolveFileFlowOption(item, selectedValue) {
      const withFiles = this.resolveAuditFlowOptionNonMulti(item, selectedValue);
      if (withFiles) {
        return withFiles;
      }
      if (this.isFieldRequired(item)) {
        return null;
      }
      const variants = this.parseAnswerVariants(item.audit_question_answer_variant, {
        questionId: item.id,
        audit_question: item.audit_question,
      });
      return variants.find((v) => v.column) || variants[0] || null;
    },

    hasAuditFileAttachments(item) {
      if (!item || item.audit_question_field_type !== 'FILE') {
        return false;
      }
      const fieldKey = this.getFieldKey(item);
      const attachments = this.auditFileAttachments[fieldKey];
      if (Array.isArray(attachments) && attachments.length > 0) {
        return true;
      }
      const value = this.itemObjCopy[fieldKey];
      return Array.isArray(value) && value.length > 0;
    },

    areRequiredVisibleFileFieldsFilled() {
      if (!this.entityList) {
        return true;
      }
      for (const item of this.activityFilteredEntityList) {
        if (item.audit_question_field_type !== 'FILE') {
          continue;
        }
        if (!item.audit_question_is_visible || !this.isQuestionVisible(item.id)) {
          continue;
        }
        if (!this.isFieldRequired(item)) {
          continue;
        }
        if (!this.hasAuditFileAttachments(item)) {
          return false;
        }
      }
      return true;
    },

    auditFileAttachmentRules(item) {
      if (item?.audit_question_field_type !== 'FILE') {
        return [];
      }
      if (!this.isFieldRequired(item)) {
        return [];
      }
      const fieldKey = this.getFieldKey(item);
      const questionId = item.id;
      return [
        () => {
          if (!this.isQuestionVisible(questionId)) {
            return true;
          }
          const n = Array.isArray(this.auditFileAttachments[fieldKey]) ? this.auditFileAttachments[fieldKey].length : 0;
          return n > 0 || this.$t('modules.customdata.audit.audit_field_required');
        },
      ];
    },

    revokeAttachmentsForFieldKey(fieldKey) {
      const list = this.auditFileAttachments[fieldKey];
      if (!Array.isArray(list)) {
        return;
      }
      list.forEach((entry) => {
        if (entry.previewUrl) {
          URL.revokeObjectURL(entry.previewUrl);
        }
      });
    },

    revokeAllAuditAttachmentPreviews() {
      Object.keys(this.auditFileAttachments || {}).forEach((k) => {
        this.revokeAttachmentsForFieldKey(k);
      });
    },

    syncFileFieldFromAttachments(fieldKey) {
      const list = this.auditFileAttachments[fieldKey];
      const files = Array.isArray(list) ? list.map((e) => e.file) : [];
      this.$set(this.itemObjCopy, fieldKey, files);
    },

    appendAuditFilesWithSource(item, fileList, source) {
      if (!item || item.audit_question_field_type !== 'FILE' || !Array.isArray(fileList) || fileList.length === 0) {
        return;
      }
      const fieldKey = this.getFieldKey(item);
      const images = fileList.filter((f) => {
        if (!(f instanceof File)) {
          return false;
        }
        const t = typeof f.type === 'string' ? f.type : '';
        if (t.startsWith('image/')) {
          return true;
        }
        const name = String(f.name || '').toLowerCase();
        return /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/.test(name);
      });
      if (images.length === 0) {
        return;
      }
      const existing = Array.isArray(this.auditFileAttachments[fieldKey]) ? [...this.auditFileAttachments[fieldKey]] : [];
      images.forEach((file) => {
        const uploadFile = normalizeAuditUploadFile(file, item, source, this.$store);
        const previewUrl = URL.createObjectURL(uploadFile);
        existing.push({
          uid: this.auditAttachmentUidSeq++,
          file: uploadFile,
          source,
          previewUrl,
        });
      });
      this.$set(this.auditFileAttachments, fieldKey, existing);
      this.syncFileFieldFromAttachments(fieldKey);
    },

    removeAuditFileAttachment(item, uid) {
      if (!item || item.audit_question_field_type !== 'FILE') {
        return;
      }
      const fieldKey = this.getFieldKey(item);
      const list = this.auditFileAttachments[fieldKey];
      if (!Array.isArray(list)) {
        return;
      }
      const next = [];
      list.forEach((entry) => {
        if (entry.uid === uid) {
          if (entry.previewUrl) {
            URL.revokeObjectURL(entry.previewUrl);
          }
        } else {
          next.push(entry);
        }
      });
      if (next.length > 0) {
        this.$set(this.auditFileAttachments, fieldKey, next);
      } else {
        this.$delete(this.auditFileAttachments, fieldKey);
      }
      this.syncFileFieldFromAttachments(fieldKey);
    },

    aggregateAttachmentSources(fieldKey) {
      const list = this.auditFileAttachments[fieldKey];
      if (!Array.isArray(list) || list.length === 0) {
        return '';
      }
      let hasCam = false;
      let hasGal = false;
      list.forEach((entry) => {
        if (entry.source === AUDIT_UPLOAD_SOURCE_CAMERA) {
          hasCam = true;
        }
        if (entry.source === AUDIT_UPLOAD_SOURCE_GALLERY) {
          hasGal = true;
        }
      });
      if (hasCam && hasGal) {
        return `${AUDIT_UPLOAD_SOURCE_CAMERA}, ${AUDIT_UPLOAD_SOURCE_GALLERY}`;
      }
      if (hasCam) {
        return AUDIT_UPLOAD_SOURCE_CAMERA;
      }
      if (hasGal) {
        return AUDIT_UPLOAD_SOURCE_GALLERY;
      }
      return '';
    },

    auditUploadSourceLabel(source) {
      if (source === AUDIT_UPLOAD_SOURCE_CAMERA) {
        return this.$t('modules.customdata.audit.audit_upload_source_camera');
      }
      if (source === AUDIT_UPLOAD_SOURCE_GALLERY) {
        return this.$t('modules.customdata.audit.audit_upload_source_gallery');
      }
      return typeof source === 'string' ? source : '';
    },

    openGalleryPick(item) {
      if (!item || item.audit_question_field_type !== 'FILE') {
        return;
      }
      this.galleryPickQuestion = item;
      const input = this.$refs.auditGalleryHiddenInput;
      if (!input || typeof input.click !== 'function') {
        return;
      }
      input.value = '';
      input.click();
    },

    onHiddenGalleryInputChange(ev) {
      const question = this.galleryPickQuestion;
      this.galleryPickQuestion = null;
      const input = ev.target;
      if (!input) {
        return;
      }
      const files = input.files ? Array.from(input.files) : [];
      input.value = '';
      if (!question || files.length === 0) {
        return;
      }
      this.appendAuditFilesWithSource(question, files, AUDIT_UPLOAD_SOURCE_GALLERY);
    },

    teardownAuditCameraCapture() {
      if (this.cameraStream) {
        try {
          this.cameraStream.getTracks().forEach((t) => t.stop());
        } catch (e) {
          // ignore
        }
        this.cameraStream = null;
      }
      const video = this.$refs.auditCameraVideo;
      if (video) {
        try {
          video.srcObject = null;
        } catch (e) {
          // ignore
        }
      }
      this.cameraCaptureStreamReady = false;
    },

    closeCameraCapture() {
      this.cameraCaptureVisible = false;
    },

    closeAuditModal() {
      this.clearFileFields();
      this.$emit('input', false);
    },

    openCameraCapture(item) {
      if (!item || item.audit_question_field_type !== 'FILE') {
        return;
      }
      if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
        if (this.isAuditMobileCaptureDevice) {
          this.triggerAuditCameraDevicePicker(item);
        } else {
          this.openGalleryPick(item);
        }
        return;
      }
      this.cameraCaptureError = '';
      this.cameraCaptureStreamReady = false;
      this.cameraCaptureFacingMode = 'environment';
      this.cameraCaptureQuestion = item;
      this.cameraCaptureVisible = true;
      this.$nextTick(() => {
        void this.startAuditCameraStream();
      });
    },

    flipAuditCameraFacing() {
      if (this.cameraCaptureShooting) {
        return;
      }
      this.cameraCaptureFacingMode =
        this.cameraCaptureFacingMode === 'environment'
          ? 'user'
          : 'environment';
      this.cameraCaptureError = '';
      this.cameraCaptureStreamReady = false;
      void this.startAuditCameraStream();
    },

    async startAuditCameraStream() {
      this.teardownAuditCameraCapture();
      if (!this.cameraCaptureVisible || !this.cameraCaptureQuestion) {
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: this.cameraCaptureFacingMode },
          },
          audio: false,
        });
        this.cameraStream = stream;
        const video = this.$refs.auditCameraVideo;
        if (!video) {
          return;
        }
        video.srcObject = stream;
        await video.play().catch(() => {});
        this.cameraCaptureStreamReady = true;
        this.cameraCaptureError = '';
      } catch (err) {
        console.error('DynamicAuditModal: camera stream', err);
        this.cameraCaptureError = this.$t('modules.customdata.audit.audit_camera_error_generic');
        this.cameraCaptureStreamReady = false;
      }
    },

    triggerAuditCameraDevicePicker(question) {
      if (!question || question.audit_question_field_type !== 'FILE') {
        return;
      }
      this.fallbackCameraPickQuestion = question;
      const input = this.$refs.auditCameraFallbackInput;
      if (!input || typeof input.click !== 'function') {
        return;
      }
      input.value = '';
      input.click();
    },

    openCameraFallbackFromDialog() {
      const q = this.cameraCaptureQuestion;
      if (!q || q.audit_question_field_type !== 'FILE') {
        return;
      }
      this.cameraCaptureVisible = false;
      this.$nextTick(() => {
        if (this.isAuditMobileCaptureDevice) {
          this.triggerAuditCameraDevicePicker(q);
        } else {
          this.openGalleryPick(q);
        }
      });
    },

    onHiddenCameraFallbackChange(ev) {
      const question = this.fallbackCameraPickQuestion;
      this.fallbackCameraPickQuestion = null;
      const input = ev.target;
      if (!input) {
        return;
      }
      const files = input.files ? Array.from(input.files) : [];
      input.value = '';
      if (!question || files.length === 0) {
        return;
      }
      // Mobile: capture="environment" — native device camera → Camera
      this.appendAuditFilesWithSource(question, files, AUDIT_UPLOAD_SOURCE_CAMERA);
    },

    async captureCameraStill() {
      const item = this.cameraCaptureQuestion;
      const video = this.$refs.auditCameraVideo;
      const canvas = this.$refs.auditCameraCanvas;
      if (!item || item.audit_question_field_type !== 'FILE' || !video || !canvas) {
        return;
      }
      if (!this.cameraCaptureStreamReady || !video.videoWidth || !video.videoHeight) {
        return;
      }
      this.cameraCaptureShooting = true;
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }
        ctx.drawImage(video, 0, 0, w, h);
        const blob = await new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.88);
        });
        if (!blob) {
          return;
        }
        const file = new File([blob], 'audit-camera-capture.jpg', { type: 'image/jpeg' });
        this.appendAuditFilesWithSource(item, [file], AUDIT_UPLOAD_SOURCE_CAMERA);
        this.cameraCaptureVisible = false;
      } finally {
        this.cameraCaptureShooting = false;
      }
    },

    getDynamicOrder(questionId) {
      return this.dynamicQuestionOrder[questionId] || null;
    },

    parseNextIds(selectedOption) {
      if (!selectedOption?.nextId) return [];
      if (typeof selectedOption.nextId === 'object' && selectedOption.nextId !== null) {
        const obj = selectedOption.nextId;
        const auditType = this.currentAuditType || 'full';
        const fallbackOrder = auditType === 'full' ? ['full', 'partial'] : ['partial', 'full'];
        for (const key of fallbackOrder) {
          if (obj[key] !== undefined) {
            const val = obj[key];
            if (typeof val === 'string' && val.includes(',')) {
              return val.split(',').map(id => parseInt(String(id).trim(), 10)).filter(n => !isNaN(n));
            }
            const num = parseInt(val, 10);
            return isNaN(num) ? [] : [num];
          }
        }
        return [];
      }
      const str = selectedOption.nextId.toString();
      if (str.includes(',')) {
        return str.split(',').map(id => parseInt(id.trim(), 10)).filter(n => !isNaN(n));
      }
      const num = parseInt(str, 10);
      return isNaN(num) ? [] : [num];
    },

    matchesActivity(option, normalizedActivity) {
      if (!normalizedActivity) return !option.activity;
      if (Array.isArray(option.activity)) return option.activity.includes(normalizedActivity);
      return option.activity === normalizedActivity;
    },

    resolveVariantByActivity(variants, selectedText, normalizedActivity) {
      const norm = normalizedActivity ?? this.activityNormalized;
      if (norm) {
        const withActivity = variants.find(v => v.text === selectedText && this.matchesActivity(v, norm));
        if (withActivity) return withActivity;
      }
      return variants.find(v => v.text === selectedText && (v.activity === undefined || v.activity === null));
    },

    getScoreFromVariant(variant) {
      if (typeof variant.score === 'object' && variant.score !== null) {
        if (this.currentAuditType && variant.score[this.currentAuditType] !== undefined) {
          return parseInt(variant.score[this.currentAuditType], 10) || 0;
        }
      }
      return parseInt(variant.score, 10) || 0;
    },

    isQuestionRelevantForActivity(item) {
      const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
      return variants.some(v => !v.activity || this.matchesActivity(v, this.activityNormalized));
    },

    isQuestionRelevantForUnified(item) {
      const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
      return variants.some(v => isDefaultAnswerVariant(v));
    },

    getDisplayVariantsForItem(item) {
      const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
      const seen = new Set();
      const result = [];
      for (const v of variants) {
        const textKey = String(v.text || '').trim();
        if (!textKey || seen.has(textKey)) continue;
        const resolved = this.resolveVariantByActivity(variants, v.text);
        if (resolved) {
          seen.add(textKey);
          result.push(resolved);
        }
      }
      return result;
    },

    // Check if field should be required based on user role exclusions
    isFieldRequired(item) {
      if (!item.audit_question_required) return false;
      
      try {
        const variants = JSON.parse(item.audit_question_answer_variant || '[]');
        if (Array.isArray(variants)) {
          const isExcluded = variants.some(variant => {
            if (!variant.exclude_necessarily_for) return false;
            
            // Handle string format: "CAM, CR, admin, assistant"
            if (typeof variant.exclude_necessarily_for === 'string') {
              const roles = variant.exclude_necessarily_for.split(',').map(role => role.trim());
              return roles.includes(this.currentUserRole);
            }
            return false;
          });
          return !isExcluded;
        }
      } catch (error) {
        console.error('Error parsing answer variants:', error);
      }
      
      return item.audit_question_required;
    },

    async send() {
      // BAT121-12: empty end → fill with now before validate so end < start is caught
      if (!(this.auditEndTimeInput && this.auditEndTimeInput.trim())) {
        this.auditEndTimeInput = this.formatTimeToHHMM(new Date());
      }
      await this.$nextTick();

      if (!this.$refs.form.validate()) {
        this.scrollToFirstAuditFormError();
        return;
      }

      if (!this.areRequiredVisibleFileFieldsFilled()) {
        this.scrollToFirstAuditFormError();
        return;
      }

      const startTime = (this.auditStartTimeInput || '').trim();
      const endTime = (this.auditEndTimeInput || '').trim();
      if (
        !this.isValidAuditTimeString(startTime)
        || !this.isValidAuditTimeString(endTime)
        || this.auditTimeToMinutes(endTime) < this.auditTimeToMinutes(startTime)
      ) {
        this.$refs.auditStartTimeField?.validate?.();
        this.$refs.auditEndTimeField?.validate?.();
        this.scrollToFirstAuditFormError();
        return;
      }

      this.loading = true;
      try {
        await this.processFileFields();

        const itemToSend = this.buildAuditDataObject();
        let tableName = this.item_obj?.audit_table_name?.trim() || null;

        if (!this.isUnifiedAuditMode) {
          if (!tableName && this.activityNormalized) {
            const activities = await this.getActivityList();
            const activityRow = activities.find(a => normalizeActivityName(a.activity_name) === this.activityNormalized);
            if (activityRow) {
              const val = activityRow.audit_table_list_name || activityRow.audit_table_name || activityRow.audit_table;
              if (val) {
                tableName = typeof val === 'object' && val?.name ? val.name : String(val).trim();
              }
            }
          }

          if (!tableName) {
            const auditTableList = await this.getAuditTableList();
            const defaultTable = auditTableList.find(table => table.id === this.defaultTableId);
            tableName = defaultTable?.audit_table_list_name;
          }
        }

        if (tableName) {
          const saveResult = await this.saveAuditData(tableName, itemToSend);
          if (saveResult) {
            this.$store.dispatch('updateAjaxDialog', [true, false, this.$t('modules.customdata.success'), this.$t('modules.customdata.audit.audit_sent_successfully')]);
            this.clearFileFields();
          } else {
            console.error('Failed to save audit data to table:', tableName);
          }
        } else {
          this.$store.dispatch('updateAjaxDialogError', [
            null,
            this.$t('common.error'),
            this.$t(this.isUnifiedAuditMode
              ? 'modules.customdata.audit.audit_unified_table_not_configured'
              : 'modules.customdata.audit.audit_activity_not_configured'),
          ]);
        }

        this.$emit('input', false);
      } catch (error) {
        this.$store.dispatch('updateAjaxDialogError', [error, 'Помилка', 'відправлення']);
      } finally {
        this.loading = false;
      }
    },

    scrollToFirstAuditFormError() {
      // Wait for Vuetify to apply error--text after validate()
      this.$nextTick(() => {
        window.setTimeout(() => {
          const root = this.$refs.auditModalBody?.$el || this.$refs.auditModalBody;
          if (!root || !root.querySelectorAll) {
            return;
          }

          const errorNodes = root.querySelectorAll('.error--text');
          for (let i = 0; i < errorNodes.length; i++) {
            const el = errorNodes[i];
            // Skip hidden stubs (e.g. MULTISELECT validation field with display:none)
            if (window.getComputedStyle(el).display === 'none') {
              continue;
            }
            const target = el.closest('.v-input') || el.closest('.question-item') || el;
            if (window.getComputedStyle(target).display === 'none') {
              continue;
            }
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }, 50);
      });
    },

    hasFileFields() {
      if (!this.entityList) return false;
      
      return this.entityList.some(item => {
        const isVisible = item.audit_question_is_visible;
        const isFile = item.audit_question_field_type === 'FILE';
        const value = this.itemObjCopy[this.getFieldKey(item)];
        const hasValue = Array.isArray(value) && 
                        value.length > 0 &&
                        (value[0] instanceof File || (Array.isArray(value[0]) && value[0].length > 0 && value[0][0] instanceof File));
        
        return isVisible && isFile && this.isQuestionVisible(item.id) && hasValue;
      });
    },

    clearFileFields() {
      if (this.entityList) {
        this.entityList.forEach(item => {
          if (item.audit_question_field_type === 'FILE') {
            const key = this.getFieldKey(item);
            this.revokeAttachmentsForFieldKey(key);
            this.$delete(this.auditFileAttachments, key);
            this.$set(this.itemObjCopy, key, []);
            this.$delete(this.fileUrls, key);
          }
        });
      }
    },

    async processFileFields() {
      if (!this.hasFileFields()) {
        return;
      }
      
      this.fileUploading = true;
      
      try {
        const fileFields = this.entityList.filter(item => {
          const value = this.itemObjCopy[this.getFieldKey(item)];
          return item.audit_question_is_visible &&
                 this.isQuestionVisible(item.id) &&
                 item.audit_question_field_type === 'FILE' &&
                 Array.isArray(value) && 
                 value.length > 0 &&
                 (value[0] instanceof File || (Array.isArray(value[0]) && value[0].length > 0 && value[0][0] instanceof File));
        });

        for (const field of fileFields) {
          const fieldKey = this.getFieldKey(field);
          const rawFiles = this.itemObjCopy[fieldKey];
          const actualFiles = this.extractFileObjects(rawFiles);

          if (actualFiles.length > 0) {
            try {
              const urls = await this.uploadFiles(actualFiles);
              if (urls && urls.length > 0) {
                this.$set(this.fileUrls, fieldKey, urls.length === 1 ? urls[0] : urls);
              }
            } catch (error) {
              console.error(`Error uploading files for field ${fieldKey}:`, error);
              throw new Error(`File upload error for field "${fieldKey}": ${error.message}`);
            }
          }
        }
      } finally {
        this.fileUploading = false;
      }
    },

    extractFileObjects(value) {
      if (!Array.isArray(value) || value.length === 0) return [];
      const result = [];
      for (const item of value) {
        if (item instanceof File) {
          result.push(item);
        } else if (Array.isArray(item) && item.length > 0 && item[0] instanceof File) {
          result.push(item[0]);
        }
      }
      return result;
    },

    async uploadFiles(files) {
      if (!files || files.length === 0) return [];
      const maxSize = 10 * 1024 * 1024;
      for (const file of files) {
        if (file.size > maxSize) {
          throw new Error(`File "${file.name}" is too large. Maximum size: 10MB`);
        }
      }

      const data = new FormData();
      files.forEach((file, i) => {
        data.set(`files[${i}]`, file, file.name);
      });

      const url = this.engine_settings.active_url;
      const token = Vue.$cookies.get('token_e');

      try {
        const response = await axios.post(
          `${url}/kw/filemanager/files/upload`,
          data,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-CSRF-TOKEN': token,
            },
          }
        );

        if (response?.data?.status === 'success' && Array.isArray(response?.data?.data) && response.data.data.length > 0) {
          return response.data.data;
        }

        throw new Error('Failed to get links to uploaded files');
      } catch (error) {
        console.error('File upload error:', error);
        throw error;
      }
    },

    async getExternalAuditEntityList() {
      const token = Vue.$cookies.get('token_e');
      const engine_url = this.engine_settings.active_url;
      const url = `${engine_url}/kw/custom_modules/${this.entityName}/list`;

      try {
        const response = await axios({
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
          },
          url: url,
          params: {
            bot_id: this.$route?.params?.id,
          },
          data: {
            limit: 9999,
            offset: 0,
          },
        });

        if (response?.data?.status === 'success') {
          return response.data.data.rows || [];
        }

      } catch (err) {
        this.$store.dispatch('updateAjaxDialogError', [err, 'Помилка', 'оновлення']);
        this.$emit('error', true);
        return [];
      }

    },

    async getActivityList() {
      const token = Vue.$cookies.get('token_e');
      const engine_url = this.engine_settings?.active_url;
      if (!engine_url) return [];
      const url = `${engine_url}/kw/custom_modules/${ACTIVITY_MODEL}/list`;
      try {
        const response = await axios({
          method: 'POST',
          headers: { 'X-CSRF-TOKEN': token },
          url,
          params: { bot_id: this.$route?.params?.id },
          data: { limit: 9999, offset: 0 },
        });
        if (response?.data?.status === 'success') {
          return response.data.data.rows || [];
        }
      } catch (err) {
        console.error('DynamicAuditModal: getActivityList', err);
      }
      return [];
    },

    async getAuditTableList() {
      const token = Vue.$cookies.get('token_e');
      const engine_url = this.engine_settings.active_url;
      const url = `${engine_url}/kw/custom_modules/${this.auditTableList}/list`;

      try {
        const response = await axios({
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
          },
          url: url,
          params: {
            bot_id: this.$route?.params?.id,
          },
          data: {
            limit: 9999,
            offset: 0,
          },
        });

        if (response?.data?.status === 'success') {
          return response.data.data.rows || [];
        }

      } catch (err) {
        this.$store.dispatch('updateAjaxDialogError', [err, 'Помилка', 'отримання списку таблиць']);
        this.$emit('error', true);
        return [];
      }

    },

    async saveAuditData(tableName, auditData) {
      const token = Vue.$cookies.get('token_e');
      const engine_url = this.engine_settings.active_url;
      const bot_id = this.$route?.params?.id;
      const url = `${engine_url}/kw/custom_modules/${tableName}/create/?bot_id=${bot_id}`;

      try {
        const response = await axios({
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
          },
          url: url,
          data: auditData,
        });

        if (response?.data?.status === 'success') {
          return response.data;
        } else {
          console.error('Failed to save audit data:', response.data);
          return null;
        }

      } catch (err) {
        console.error('Error saving audit data:', err);
        this.$store.dispatch('updateAjaxDialogError', [err, 'Помилка', 'збереження даних аудиту']);
        return null;
      }
    },

    /**
     * Format time to HH:MM format
     * @param {Date} date - Date object to format
     * @returns {string} - Formatted time string (HH:MM)
     */
    formatTimeToHHMM(date) {
      if (!date) return null;
      
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${hours}:${minutes}`;
    },

    isValidAuditTimeString(value) {
      return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
    },

    auditTimeToMinutes(value) {
      const [hours, minutes] = String(value).split(':').map(Number);
      return (hours * 60) + minutes;
    },

    onAuditStartTimeInput() {
      this.$nextTick(() => {
        this.$refs.auditEndTimeField?.validate?.();
      });
    },

    confirmAuditStartTimePicker() {
      this.$refs.dialogAuditStartTime.save(this.auditStartTimeInput);
      this.onAuditStartTimeInput();
    },

    confirmAuditEndTimePicker() {
      this.$refs.dialogAuditEndTime.save(this.auditEndTimeInput);
      this.$nextTick(() => {
        this.$refs.auditEndTimeField?.validate?.();
      });
    },

    buildAuditDataObject() {
      const currentTime = new Date();
      
      const auditData = {
        "audit_score": this.totalScorePercentage.toString(),
        "author_role_id": this.itemObjCopy.author_role_id,
        "author_id": this.itemObjCopy.author_id,
        "supervisor_name": this.itemObjCopy.supervisor_name,
        "consultant_name": this.itemObjCopy.consultant_name,
        "workshift_id": this.itemObjCopy.workshift_id,
        "petra_id": this.itemObjCopy.petra_id,
        "audit_date": new Date().toISOString().split('T')[0],
        "audit_start": (this.auditStartTimeInput && this.auditStartTimeInput.trim()) ? this.auditStartTimeInput.trim() : this.formatTimeToHHMM(this.auditStartTime),
        "audit_finish": (this.auditEndTimeInput && this.auditEndTimeInput.trim()) ? this.auditEndTimeInput.trim() : this.formatTimeToHHMM(currentTime),
        "audit_type": this.currentAuditType || "",
        "kw_user_id": this.itemObjCopy.kw_user_id || null,
        "email": this.itemObjCopy.email,
        "additional_crud_filter": true,
      };

      if (!this.isUnifiedAuditMode) {
        if (this.itemObjCopy.activity_name !== undefined) {
          auditData.activity_name = this.itemObjCopy.activity_name;
        }
        if (this.itemObjCopy.activity_normalized !== undefined) {
          auditData.activity_normalized = this.itemObjCopy.activity_normalized;
        }
      }

      // Process all questions to extract column-value pairs
      if (this.entityList) {
        this.activityFilteredEntityList.forEach(item => {
          if (item.audit_question_is_visible && this.isQuestionVisible(item.id)) {
            const fieldKey = this.getFieldKey(item);
            const userAnswer = this.itemObjCopy[fieldKey];


            
            if (userAnswer !== undefined && userAnswer !== null && userAnswer !== '' && 
                !(typeof userAnswer === 'object' && Object.keys(userAnswer).length === 0)) {
              const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });

              
              const isReservedTimeColumn = (col) => col === 'audit_start' || col === 'audit_finish';

              if (item.audit_question_field_type === 'MULTISELECT') {
                const selectedValues = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
                selectedValues.forEach(selectedText => {
                  const selectedOption = this.resolveVariantByActivity(variants, selectedText);
                  if (selectedOption && selectedOption.column && !isReservedTimeColumn(selectedOption.column)) {
                    auditData[selectedOption.column] = selectedOption.value !== null ? selectedOption.value : selectedText;
                  }
                });
              } else {
                const selectedOption = this.resolveVariantByActivity(variants, userAnswer);
                if (selectedOption && selectedOption.column && !isReservedTimeColumn(selectedOption.column)) {
                  // Use value if available, otherwise use the user answer
                  const finalValue = selectedOption.value !== null ? selectedOption.value : userAnswer;
                  auditData[selectedOption.column] = finalValue;
                } else if (item.audit_question_field_type === 'TEXT' || item.audit_question_field_type === 'STRING') {
                  // For text fields, use the column from variants if available
                  const textOption = variants.find(option => option.column);
                  if (textOption && textOption.column && !isReservedTimeColumn(textOption.column)) {
                    auditData[textOption.column] = userAnswer;

                  }
                } else if (item.audit_question_field_type === 'BOOLEAN') {
                  // For boolean fields, use the column from variants if available
                  const booleanOption = variants.find(option => option.column);
                  if (booleanOption && booleanOption.column && !isReservedTimeColumn(booleanOption.column)) {
                    auditData[booleanOption.column] = userAnswer;
                  }
                } else if (item.audit_question_field_type === 'FILE') {
                  const fileOption = variants.find(option => option.column);
                  if (fileOption && fileOption.column && !isReservedTimeColumn(fileOption.column)) {
                    const urls = this.fileUrls[this.getFieldKey(item)];
                    if (urls) {
                      auditData[fileOption.column] = Array.isArray(urls)
                        ? (urls.length === 1 ? urls[0] : JSON.stringify(urls))
                        : urls;
                      const uploadSrcColRaw = typeof fileOption.upload_source_column === 'string' ? fileOption.upload_source_column.trim() : '';
                      if (uploadSrcColRaw) {
                        const aggText = this.aggregateAttachmentSources(this.getFieldKey(item));
                        if (aggText) {
                          auditData[uploadSrcColRaw] = aggText;
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        });
      }

      const allQuestionColumns = this.getAllQuestionColumns();
      allQuestionColumns.forEach(column => {
        if (!(column in auditData)) {
          auditData[column] = null;
        }
      });

      // Always take time from our time inputs (never overwritten by question columns)
      auditData.audit_start = (this.auditStartTimeInput && this.auditStartTimeInput.trim())
        ? this.auditStartTimeInput.trim()
        : this.formatTimeToHHMM(this.auditStartTime);
      auditData.audit_finish = (this.auditEndTimeInput && this.auditEndTimeInput.trim())
        ? this.auditEndTimeInput.trim()
        : this.formatTimeToHHMM(currentTime);

      return auditData;
    },

    getAllQuestionColumns() {
      const columns = new Set();
      
      if (this.entityList) {
        this.activityFilteredEntityList.forEach(item => {
          if (item.audit_question_is_visible && this.isQuestionVisible(item.id)) {
            const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
            const variantsForColumns = this.isUnifiedAuditMode
              ? variants.filter(v => isDefaultAnswerVariant(v))
              : variants;
            variantsForColumns.forEach(variant => {
              if (variant.column) {
                columns.add(variant.column);
              }
              if (item.audit_question_field_type === 'FILE') {
                const uploadCol = variant.upload_source_column != null ? String(variant.upload_source_column).trim() : '';
                if (uploadCol) {
                  columns.add(uploadCol);
                }
              }
            });
          }
        });
      }
      
      return Array.from(columns);
    },

         initializeDefaultValues() {
       if (!this.entityList) return;
       
       this.entityList.forEach(item => {
         if (item.audit_question_is_visible) {
           const key = this.getFieldKey(item);
           const legacyKey = item.audit_question;
           if (item.audit_question_default !== undefined) {
             this.$set(this.itemObjCopy, key, item.audit_question_default);
           } else if ((key !== legacyKey) && (legacyKey in this.itemObjCopy)) {
             this.$set(this.itemObjCopy, key, this.itemObjCopy[legacyKey]);
           } else if (item.audit_question_field_type === 'MULTISELECT') {
             this.$set(this.itemObjCopy, key, this.itemObjCopy[key] || []);
           } else if (item.audit_question_field_type === 'FILE') {
             this.$set(this.itemObjCopy, key, this.itemObjCopy[key] || []);
           } else if (['RADIO', 'SELECT', 'BOOLEAN'].includes(item.audit_question_field_type)) {
             if ((key !== legacyKey) && (legacyKey in this.itemObjCopy)) {
               this.$set(this.itemObjCopy, key, this.itemObjCopy[legacyKey]);
             } else if (!(key in this.itemObjCopy)) {
               this.$set(this.itemObjCopy, key, item.audit_question_field_type === 'BOOLEAN' ? null : '');
             }
           }
         }
       });
       
       // Initialize audit type if there's a default answer with auditType
       this.initializeAuditType();
     },
     
     initializeAuditType() {
       if (!this.entityList) return;
       
       for (const item of this.activityFilteredEntityList) {
         const key = this.getFieldKey(item);
         if (item.audit_question_is_visible && this.itemObjCopy[key]) {
           const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
           const selectedOption = this.resolveVariantByActivity(variants, this.itemObjCopy[key]);
           
           if (selectedOption && selectedOption.auditType) {
             this.currentAuditType = selectedOption.auditType;
             break;
           }
         }
       }
     },

      parseAnswerVariants(answerVariantString, context = {}) {
       try {
         if (!answerVariantString) return [];
         
         const variants = JSON.parse(answerVariantString);
         
         if (Array.isArray(variants)) {
           return variants.map(variant => ({
             text: variant.text || '',
             nextId: variant.nextId || '',
             score: variant.score !== undefined ? variant.score : 0,
             auditType: variant.auditType || null,
             column: variant.column || null,
             value: variant.value !== undefined ? variant.value : null,
             exclude_necessarily_for: variant.exclude_necessarily_for || null,
             activity: variant.activity ?? null,
             upload_source_column: variant.upload_source_column != null && variant.upload_source_column !== undefined
               ? String(variant.upload_source_column).trim() || null
               : null,
           }));
         }
         
         return [];
       } catch (error) {
         const ctx = Object.keys(context).length ? context : { note: 'add context at call site' };
         const preview = typeof answerVariantString === 'string'
           ? (answerVariantString.length > 600 ? answerVariantString.slice(0, 600) + '...[truncated]' : answerVariantString)
           : String(answerVariantString);
         console.error('[DynamicAuditModal] Error parsing answer variants:', {
           context: ctx,
           errorMessage: error?.message,
           parsePosition: error?.message?.match(/position (\d+)/)?.[1],
           rawPreview: preview,
           rawLength: typeof answerVariantString === 'string' ? answerVariantString.length : 0,
         }, error);
         return [];
       }
     },

              isQuestionVisible(questionId) {
       // If it's the first question, show it
       if (questionId === 1) return true;
       
       // Check if there's a previous question with an answer that leads to this question
       for (const item of this.activityFilteredEntityList) {
         const fieldKey = this.getFieldKey(item);
         if (item.audit_question_is_visible && this.itemObjCopy[fieldKey]) {
           const selectedValue = this.itemObjCopy[fieldKey];
           const variants = this.parseAnswerVariants(item.audit_question_answer_variant, { questionId: item.id, audit_question: item.audit_question });
           
           let selectedOption = null;
           if (item.audit_question_field_type === 'MULTISELECT') {
             const selectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
             for (const sv of selectedValues) {
               const opt = this.resolveVariantByActivity(variants, sv);
               if (opt && opt.nextId) {
                 selectedOption = opt;
                 break;
               }
             }
           } else if (item.audit_question_field_type === 'FILE') {
             selectedOption = this.resolveFileFlowOption(item, selectedValue);
           } else {
             selectedOption = this.resolveAuditFlowOptionNonMulti(item, selectedValue);
           }
           
           if (selectedOption && selectedOption.nextId) {
             const nextIds = this.parseNextIds(selectedOption);
             if (nextIds.includes(questionId)) {
               return true;
             }
           }
         }
       }
       
       return false;
     },

              resetSubsequentAnswers(changedQuestionId) {
       if (!this.entityList || this.isResetting) return;
       
       this.isResetting = true;
       
       // Check if the changed question has auditType and update currentAuditType
       const changedQuestion = this.entityList.find(item => item.id === changedQuestionId);
      if (changedQuestion) {
        const fieldKeyForChanged = this.getFieldKey(changedQuestion);
        const selectedValue = this.itemObjCopy[fieldKeyForChanged];
        if (selectedValue !== undefined && selectedValue !== null && selectedValue !== '' && !(typeof selectedValue === 'object' && Array.isArray(selectedValue) && selectedValue.length === 0)) {
          const variants = this.parseAnswerVariants(changedQuestion.audit_question_answer_variant, { questionId: changedQuestion.id, audit_question: changedQuestion.audit_question });
          let selectedOption = null;
          if (changedQuestion.audit_question_field_type === 'FILE') {
            selectedOption = this.resolveAuditFlowOptionNonMulti(changedQuestion, selectedValue);
          } else {
            selectedOption = this.resolveVariantByActivity(variants, selectedValue);
          }

          if (selectedOption && selectedOption.auditType) {
            this.currentAuditType = selectedOption.auditType;
          } else {
            const hasAuditTypeOptions = variants.some(option => option.auditType);
            if (hasAuditTypeOptions) {
              this.currentAuditType = null;
            }
          }
        }
      }

      const orderMap = this.dynamicQuestionOrder;
       const changedOrder = orderMap[changedQuestionId];
       let questionsAfterChanged;
       if (changedOrder !== undefined) {
         // Clear: 1) questions with order > changedOrder (subsequent in flow)
         // 2) questions not in current flow (orderMap[id] undefined - from other branch, stale answers)
         questionsAfterChanged = this.activityFilteredEntityList.filter(item => {
           if (!item.audit_question_is_visible) return false;
           const order = orderMap[item.id];
           return order === undefined || order > changedOrder;
         });
       } else {
         questionsAfterChanged = this.activityFilteredEntityList.filter(item =>
           item.audit_question_is_visible && item.id > changedQuestionId
         );
       }
       
      questionsAfterChanged.forEach(question => {
        const fieldKey = this.getFieldKey(question);
        // Don't reset TEXT fields - they should preserve their content
        if (question.audit_question_field_type === 'TEXT' || question.audit_question_field_type === 'STRING') {

          return;
        }
        
        if (question.audit_question_field_type === 'FILE') {
          return;
        }

        // Initialize with appropriate default value based on field type
        let defaultValue;
        if (question.audit_question_field_type === 'MULTISELECT') {
          defaultValue = [];
        } else if (question.audit_question_field_type === 'FILE') {
          defaultValue = []; // v-file-input expects array
        } else {
          defaultValue = '';
        }
        this.$set(this.itemObjCopy, fieldKey, defaultValue);
      });
       
       this.$nextTick(() => {
         this.isResetting = false;
       });
     },
     
          getNextIdsInfo(answerVariantString) {
       try {
         if (!answerVariantString) return 'None';
         
         const variants = JSON.parse(answerVariantString);
         if (!Array.isArray(variants)) return 'Invalid format';
         
         const nextIds = variants
           .filter(variant => variant.nextId)
           .map(variant => {
             let nextIdInfo = '';
             
             if (typeof variant.nextId === 'object' && variant.nextId !== null) {
               // Dynamic nextId based on audit type
               const auditTypes = Object.keys(variant.nextId);
               nextIdInfo = auditTypes.map(type => `${type}: ${variant.nextId[type]}`).join(', ');
               nextIdInfo = `{${nextIdInfo}}`;
             } else {
               // Simple nextId
               nextIdInfo = variant.nextId.toString();
             }
             
             return `${variant.text}: ${nextIdInfo}`;
           })
           .join(', ');
         
         return nextIds || 'None';
       } catch (error) {
         return 'Parse error';
       }
     },

     toggleDescription(questionId) {
       this.$set(this.visibleDescriptions, questionId, !this.visibleDescriptions[questionId]);
     },

  },

  beforeDestroy() {
    this.teardownAuditCameraCapture();
    this.revokeAllAuditAttachmentPreviews();
    this.galleryPickQuestion = null;
    this.fallbackCameraPickQuestion = null;
  },

  async mounted() {
    setTimeout(async () => {
      this.entityList = await this.getExternalAuditEntityList();
      if (this.entityList && this.entityList.length > 0) {
        this.initializeDefaultValues();
      }
    }, 500);
  },

};
</script>

<style scoped>
.audit-time-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.audit-time-info .v-chip {
  font-size: 0.75rem;
  height: 24px;
}

.audit-time-info .v-icon {
  font-size: 14px;
}

.custom-audit-modal-grouped .v-card-title {
  padding-bottom: 16px;
}

.custom-audit-modal-grouped .v-card-title h5 {
  margin-bottom: 0;
}
</style>

<style scoped lang="scss">
/* Question group styling */
.question-group {
  margin-bottom: 2rem;
}

/* Special styling for ungrouped questions */
.question-group:has(.group-header) {
  margin-bottom: 2rem;
}

.question-group:not(:has(.group-header)) {
  margin-bottom: 1rem;
}

.group-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8eaf6 100%);
  border-left: 4px solid #1976d2;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
}

.group-header small {
  display: block;
  margin-top: 4px;
  opacity: 0.7;
}

.question-actions small {
  display: block;
  opacity: 0.7;
  font-size: 12px;
}

.question-row {
    display: flex;
}

.question-item {
  border: 1px solid #e0e0e0;
  background-color: #fafafa;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.question-item:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  border-color: #1976d2;
  transform: translateY(-1px);
}

/* Enhanced dialog styling */
.custom-audit-modal-grouped .v-dialog {
  border-radius: 12px;
}

.custom-audit-modal-grouped .v-card {
  border-radius: 12px;
  overflow: hidden;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .question-item {
    padding: 1rem !important;
  }
  
  .group-header {
    padding: 1rem !important;
  }
  
  .custom-audit-modal-grouped .v-dialog {
    max-width: 95vw !important;
  }
}

/* Animation for group headers */
.group-header {
  animation: slideInLeft 0.5s ease-out;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Question item animation */
.question-item {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Better spacing for ungrouped questions */
.question-group:not(:has(.group-header)) .question-item:first-child {
  margin-top: 0;
}

.question-group:not(:has(.group-header)) .question-item:last-child {
  margin-bottom: 0;
}

 /* Radio button styling */
 .v-radio-group {
   .v-radio {
     margin-bottom: 8px;
     
     .v-label {
       font-size: 14px;
       color: var(--v-text--primary);
     }
   }
 }

 /* Multiselect styling */
 .multiselect-item {
   .v-label {
     font-size: 14px;
     color: var(--v-text--primary);
     line-height: 1.4;
   }
   
   .v-input--selection-controls__input {
     margin-right: 8px;
   }
 }

 /* Info icon styling */
 .info-icon {
   cursor: pointer;
   transition: all 0.2s ease;
   
   &:hover {
     transform: scale(1.1);
   }
 }

   /* Total score styling */
  .total-score {
    color: var(--v-primary-base);
    padding: 8px 12px;
    background: rgba(25, 118, 210, 0.1);
    border-radius: 6px;
    border-left: 4px solid var(--v-primary-base);
  }
  
  /* Audit type info styling */
  .audit-type-info {
    font-size: 12px;
    color: var(--v-secondary-base);
    font-weight: 500;
    background: rgba(156, 39, 176, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid var(--v-secondary-base);
  }

  /* Question title styling */
  .question-title {
    line-height: 1.2;
    
    @media screen and (max-width: 600px) {
      font-size: 18px !important;
    }
  }

 /* Question description styling */
 .question-description {
   font-style: italic;
   border-left: 4px solid var(--v-primary-base);
   padding: 12px 16px;
   font-size: 15px;
   background: linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.1) 100%);
   border-radius: 6px;
   line-height: 1.3;
   color: var(--v-text--primary);
 }

/* Audit completed message styling */
.audit-completed-message {
  .v-alert {
    background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
  }
}

.audit-file-picker__hidden-input {
	position: fixed;
	left: -9999px;
	width: 1px;
	height: 1px;
	opacity: 0;
	overflow: hidden;
}

.audit-file-picker__actions {
	gap: 8px;
}

.audit-file-picker__previews {
	gap: 10px;
}

.audit-file-picker__thumb {
	position: relative;
	max-width: 108px;
}

.audit-file-picker__remove.v-btn.v-btn--icon {
	position: absolute;
	top: -6px;
	right: -6px;
	min-width: 24px !important;
	width: 24px !important;
	height: 24px !important;
	background-color: #fff !important;
	border: 1px solid rgba(0, 0, 0, 0.42) !important;
	box-shadow: none !important;
}

/* Camera dialog actions: narrow = full-width capture first, file + cancel row below */
.audit-camera-dialog-actions {
	flex-direction: column-reverse;
	align-items: stretch !important;
	flex-wrap: nowrap;
	gap: 8px;
}

.audit-camera-dialog-actions__capture {
	width: 100%;
}

.audit-camera-dialog-actions__secondary {
	width: 100%;
	min-width: 0;
}

@media screen and (min-width: 600px) {
	.audit-camera-dialog-actions {
		flex-direction: row;
		flex-wrap: wrap;
		align-items: center !important;
	}

	.audit-camera-dialog-actions__capture {
		width: auto;
		flex-shrink: 0;
	}

	.audit-camera-dialog-actions__secondary {
		width: auto;
		flex: 1 1 auto;
	}
}
</style>